import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CompanyHistory = () => {
  const [selectedYear, setSelectedYear] = useState(2024);

  const timeline = [
    {
      year: 2020,
      title: 'Foundation & Vision',
      description: 'RCEG AI Ventures founded with a mission to democratize AI for enterprises. Established core team of AI researchers and business consultants.',
      achievements: ['Founded by AI research veterans', 'First 5 enterprise clients secured', 'Core AI methodology developed'],
      icon: 'Rocket'
    },
    {
      year: 2021,
      title: 'Rapid Growth & Recognition',
      description: 'Expanded service offerings to include cybersecurity and software development. Achieved significant client growth and industry recognition.',
      achievements: ['Grew to 20+ enterprise clients', 'ISO 27001 certification achieved', 'First major industry award received'],
      icon: 'TrendingUp'
    },
    {
      year: 2022,
      title: 'Scale & Innovation',
      description: 'Launched advanced AI solutions and expanded team capabilities. Established partnerships with leading technology providers.',
      achievements: ['100+ projects delivered', 'Strategic partnerships formed', 'Advanced AI research lab established'],
      icon: 'Zap'
    },
    {
      year: 2023,
      title: 'Market Leadership',
      description: 'Became recognized market leader in enterprise AI consulting. Expanded international presence and service portfolio.',
      achievements: ['40+ enterprise clients', 'International expansion', 'Industry thought leadership recognition'],
      icon: 'Crown'
    },
    {
      year: 2024,
      title: 'Future-Ready Excellence',
      description: 'Continuing to push boundaries in AI innovation while maintaining our commitment to client success and ethical AI practices.',
      achievements: ['50+ enterprise clients', '98% client retention rate', 'Next-generation AI solutions launched'],
      icon: 'Sparkles'
    }
  ];

  const selectedMilestone = timeline?.find(item => item?.year === selectedYear);

  return (
    <section>
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-foreground mb-4">Our Journey</h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          From startup to industry leader, our timeline reflects continuous innovation and client success
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-soft border border-border p-8 lg:p-12">
        {/* Timeline Navigation */}
        <div className="flex justify-center mb-12">
          <div className="flex items-center space-x-4 bg-muted rounded-full p-2">
            {timeline?.map((item) => (
              <button
                key={item?.year}
                onClick={() => setSelectedYear(item?.year)}
                className={`px-4 py-2 rounded-full font-medium transition-smooth ${
                  selectedYear === item?.year
                    ? 'bg-primary text-white shadow-soft'
                    : 'text-muted-foreground hover:text-foreground hover:bg-white'
                }`}
              >
                {item?.year}
              </button>
            ))}
          </div>
        </div>

        {/* Selected Milestone */}
        {selectedMilestone && (
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 rounded-full gradient-primary flex items-center justify-center">
                  <Icon name={selectedMilestone?.icon} size={20} color="white" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary">{selectedMilestone?.year}</div>
                  <div className="text-sm text-muted-foreground">Milestone Year</div>
                </div>
              </div>
              
              <h3 className="text-2xl font-bold text-foreground mb-4">
                {selectedMilestone?.title}
              </h3>
              
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {selectedMilestone?.description}
              </p>

              <div className="space-y-3">
                <h4 className="font-semibold text-foreground">Key Achievements:</h4>
                {selectedMilestone?.achievements?.map((achievement, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 rounded-full bg-primary"></div>
                    <span className="text-muted-foreground">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl p-8">
                {/* Visual Timeline */}
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-primary mb-2">
                      {selectedMilestone?.year}
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      ``` html
                      <div 
                        className="h-full gradient-primary transition-all duration-1000 ease-out"
                        style={{ width: `${((selectedMilestone?.year - 2020) / 4) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Progress Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-white rounded-lg">
                      <div className="text-2xl font-bold text-primary mb-1">
                        {selectedMilestone?.year === 2020 ? '5' : 
                         selectedMilestone?.year === 2021 ? '20' :
                         selectedMilestone?.year === 2022 ? '30' :
                         selectedMilestone?.year === 2023 ? '40' : '50'}+
                      </div>
                      <div className="text-xs text-muted-foreground">Clients</div>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg">
                      <div className="text-2xl font-bold text-primary mb-1">
                        {selectedMilestone?.year === 2020 ? '10' : 
                         selectedMilestone?.year === 2021 ? '50' :
                         selectedMilestone?.year === 2022 ? '100' :
                         selectedMilestone?.year === 2023 ? '300' : '500'}+
                      </div>
                      <div className="text-xs text-muted-foreground">Projects</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Icon */}
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-accent rounded-full flex items-center justify-center shadow-soft">
                <Icon name={selectedMilestone?.icon} size={24} color="white" />
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CompanyHistory;